import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useLanguage, t } from '../contexts/LanguageContext';
import {
  CalendarDays, Clock, Video, Timer, Sparkles, ArrowRight
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } })
};

function getNextSunday() {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? 7 : 7 - day;
  const d = new Date(now);
  d.setDate(now.getDate() + diff);
  return d;
}

function getNextSaturday() {
  const sun = getNextSunday();
  const sat = new Date(sun);
  sat.setDate(sun.getDate() - 1);
  return sat;
}

function formatDate(d) {
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const day = d.getDate();
  const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
  return `${day}${suffix} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function HeroSection({ seatsData, pricing }) {
  const [tab, setTab] = useState('morning');
  const nextSunday = useMemo(() => getNextSunday(), []);
  const nextSaturday = useMemo(() => getNextSaturday(), []);
  const navigate = useNavigate();
  const { lang } = useLanguage();

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-indigo-100 via-purple-50 to-white text-slate-900" data-testid="hero-section">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-96 h-96 bg-indigo-200 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-amber-100 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-18">
        {/* Top badge */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0} className="text-center mb-6">
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-600/10 border border-indigo-200 text-indigo-700 text-sm sm:text-base font-semibold">
            <Sparkles className="w-4 h-4 text-amber-500" />
            {t('heroBadge', lang)}
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={1} className="font-serif text-3xl sm:text-4xl lg:text-[3.2rem] text-center leading-[1.15] tracking-tight mb-4 max-w-4xl mx-auto text-slate-900">
          {t('heroHeadline1', lang)}{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">{t('heroHeadline2', lang)}</span>
        </motion.h1>

        <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={2} className="text-center text-lg sm:text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
          {t('heroSocial', lang)} <strong className="text-indigo-700">{t('heroSocialBold', lang)}</strong> {t('heroSocialEnd', lang)}
        </motion.p>

        {/* Hero Grid: Coach Image + Session Card */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
          {/* Left - Coach Circle Photo */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="flex flex-col items-center">
            <div className="relative w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] lg:w-[360px] lg:h-[360px]">
              <div className="absolute inset-[-16px] rounded-full border-2 border-dashed border-indigo-300/50" />
              <div className="absolute inset-[-8px] rounded-full border border-dashed border-amber-300/40" />
              <div className="absolute -top-2 left-1/2 w-3 h-3 rounded-full bg-indigo-400/40" />
              <div className="absolute -bottom-2 left-1/2 w-3 h-3 rounded-full bg-amber-400/40" />
              <div className="absolute top-1/2 -left-2 w-3 h-3 rounded-full bg-purple-400/30" />
              <div className="absolute top-1/2 -right-2 w-3 h-3 rounded-full bg-indigo-400/30" />
              <div className="w-full h-full rounded-full overflow-hidden border-4 border-white shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_manifest-dinesh/artifacts/bca2afwc_12.png"
                  alt="Dr. Dinesh Mungath"
                  className="w-full h-full object-cover object-top"
                  data-testid="hero-coach-image"
                />
              </div>
            </div>
            <div className="text-center mt-6 max-w-sm">
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-slate-900" data-testid="hero-coach-name">
                {t('coachName', lang)}
              </h3>
              <p className="text-sm sm:text-base text-slate-500 mt-2 leading-relaxed font-medium" data-testid="hero-coach-credentials">
                {t('coachCredentials', lang)}
              </p>
            </div>
          </motion.div>

          {/* Right - Session Card */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4}>
            <div className="flex gap-3 mb-5 justify-center lg:justify-start">
              <button
                onClick={() => setTab('morning')}
                className={`px-6 py-3 rounded-full text-sm sm:text-base font-semibold transition-all ${tab === 'morning' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid="tab-morning"
              >
                {t('morning', lang)}
              </button>
              <button
                onClick={() => setTab('evening')}
                className={`px-6 py-3 rounded-full text-sm sm:text-base font-semibold transition-all ${tab === 'evening' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid="tab-evening"
              >
                {t('evening', lang)}
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5 shadow-xl" data-testid="session-details-card">
              <div className="grid grid-cols-2 gap-5">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <CalendarDays className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-slate-400 font-medium uppercase tracking-wider">{t('date', lang)}</p>
                    <p className="text-slate-900 font-semibold text-sm sm:text-base" data-testid="session-date">
                      {tab === 'morning' ? formatDate(nextSunday) : formatDate(nextSaturday)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-slate-400 font-medium uppercase tracking-wider">{t('time', lang)}</p>
                    <p className="text-slate-900 font-semibold text-sm sm:text-base" data-testid="session-time">
                      {tab === 'morning' ? t('morningTime', lang) : t('eveningTime', lang)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-slate-400 font-medium uppercase tracking-wider">{t('platform', lang)}</p>
                    <p className="text-slate-900 font-semibold text-sm sm:text-base">{t('onZoom', lang)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Timer className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-slate-400 font-medium uppercase tracking-wider">{t('duration', lang)}</p>
                    <p className="text-slate-900 font-semibold text-sm sm:text-base">{t('threeHours', lang)}</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate(`/checkout?session=${tab}`)}
                className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 text-lg font-bold shadow-lg shadow-indigo-600/20 transition-all hover:scale-[1.02]"
                data-testid="signup-now-btn"
              >
                {t('signUpNow', lang)} &#8377;{pricing?.price_inr || 499}/- <ArrowRight className="w-5 h-5 ml-1" />
              </Button>

              <div className="bg-red-50 border border-red-100 rounded-xl p-3" data-testid="seats-counter">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-red-700">{t('seatsFilling', lang)}</span>
                  <span className="text-sm font-bold text-red-600">{seatsData.remaining} {t('seatsLeft', lang)}</span>
                </div>
                <div className="w-full bg-red-100 rounded-full h-2.5">
                  <div
                    className="bg-red-500 h-2.5 rounded-full transition-all duration-1000"
                    style={{ width: `${Math.min((seatsData.filled / 500) * 100, 98)}%` }}
                  />
                </div>
                <p className="text-xs text-red-500 mt-1">{seatsData.filled} {t('alreadyRegistered', lang)}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
